import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircleQuestion, X, Send, Star, Minimize2, Paperclip, Download, File, Image as ImageIcon } from "lucide-react";
import { getSupportSessionChannel, releaseSupportSessionChannel, subscribeToReconnect } from "@/lib/pusher-client";
import { useUpload } from "@/hooks/use-upload";
import type { SupportAttachment, SupportMessagePayload } from "@shared/pusher-events";
import { normalizeSupportAttachment, attachmentUrl } from "@shared/support-chat-utils";

interface ChatMessage {
  id: string;
  sessionId: string;
  senderId: string;
  senderRole: string;
  senderName?: string;
  body: string;
  attachments?: SupportAttachment[];
  createdAt: string;
}

interface ChatSession {
  id: string;
  userId: string;
  status: string;
  subject: string | null;
  startedAt: string;
  endedAt: string | null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentBubble({ attachment }: { attachment: SupportAttachment }) {
  const url = attachmentUrl(attachment.objectPath);
  if (attachment.contentType.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block mt-1.5">
        <img src={url} alt={attachment.filename} className="max-w-[180px] max-h-[120px] rounded border object-cover" />
        <span className="text-[10px] opacity-70 flex items-center gap-1 mt-0.5">
          <ImageIcon className="w-3 h-3" /> {attachment.filename}
        </span>
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 mt-1.5 p-1.5 rounded border bg-background/50 hover:bg-background/80 transition-colors"
    >
      <File className="w-4 h-4 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs truncate">{attachment.filename}</p>
        <p className="text-[10px] opacity-60">{formatFileSize(attachment.size)}</p>
      </div>
      <Download className="w-3 h-3 shrink-0 ml-auto opacity-50" />
    </a>
  );
}

/** Normalize all attachments on a raw message from the REST API or Pusher. */
function normalizeMessageAttachments(raw: unknown[] | undefined): SupportAttachment[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeSupportAttachment).filter((a): a is SupportAttachment => a !== null);
}

export function SupportChatWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [closedSessionId, setClosedSessionId] = useState<string | null>(null);
  const showRating = closedSessionId !== null;
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [hasUnread, setHasUnread] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<SupportAttachment[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionChannelRef = useRef<any>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadFile, isUploading } = useUpload({
    onSuccess: (response) => {
      setPendingAttachments((prev) => [...prev, {
        objectPath: response.objectPath,
        filename: response.metadata.name,
        contentType: response.metadata.contentType,
        size: response.metadata.size,
      }]);
    },
  });

  const { data: activeSession, refetch: refetchSession } = useQuery<ChatSession | null>({
    queryKey: ["/api/support-chat/sessions", "active"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/support-chat/sessions");
      const sessions = await res.json();
      const active = sessions.find((s: ChatSession) => s.status === "active" || s.status === "waiting");
      return active || null;
    },
    enabled: !!user && user.role !== "super_admin",
  });

  const { data: sessionMessages } = useQuery<ChatMessage[]>({
    queryKey: ["/api/support-chat/sessions", activeSession?.id, "messages"],
    queryFn: async () => {
      if (!activeSession?.id) return [];
      const res = await apiRequest("GET", `/api/support-chat/sessions/${activeSession.id}/messages`);
      const raw = await res.json();
      // Normalize attachments from DB records (may be canonical or legacy shape)
      return (raw as any[]).map((msg: any) => ({
        ...msg,
        attachments: normalizeMessageAttachments(msg.attachments),
      })) as ChatMessage[];
    },
    enabled: !!activeSession?.id,
  });

  useEffect(() => {
    if (sessionMessages) {
      setMessages(prev => {
        const optimistic = prev.filter(m => m.id.startsWith("temp-"));
        if (optimistic.length === 0) return sessionMessages;
        const stillPending = optimistic.filter(m => {
          return !sessionMessages.some(sm => sm.body === m.body && sm.senderId === m.senderId);
        });
        return [...sessionMessages, ...stillPending];
      });
    }
  }, [sessionMessages]);

  useEffect(() => {
    if (activeSession?.id && pendingMessageRef.current) {
      const pending = pendingMessageRef.current;
      pendingMessageRef.current = null;
      sendMessageMutation.mutate({ body: pending.body, attachments: pending.attachments, sessionId: activeSession.id });
    }
  }, [activeSession?.id]);

  const sessionId = activeSession?.id || closedSessionId;

  useEffect(() => {
    if (!sessionId) return;
    let disposed = false;
    let reconciliationVersion = 0;
    const channel = getSupportSessionChannel(sessionId);
    sessionChannelRef.current = channel;

    const handleMsg = (msg: SupportMessagePayload) => {
      if (msg.isInternal) return;
      const normalizedMsg: ChatMessage = {
        id: msg.id,
        sessionId: msg.sessionId,
        senderId: msg.senderId,
        senderRole: msg.senderRole,
        senderName: msg.senderName ?? undefined,
        body: msg.body,
        attachments: normalizeMessageAttachments(msg.attachments),
        createdAt: msg.createdAt,
      };
      setMessages((prev) => {
        if (prev.some((m) => m.id === normalizedMsg.id)) return prev;
        const withoutTemp = prev.filter((m) => !(m.id.startsWith("temp-") && m.body === normalizedMsg.body && m.senderId === normalizedMsg.senderId));
        return [...withoutTemp, normalizedMsg];
      });
      if (!isOpen && normalizedMsg.senderId !== user?.id) {
        setHasUnread(true);
      }
    };

    const reconcileSession = async () => {
      const version = ++reconciliationVersion;
      try {
        const res = await apiRequest("GET", `/api/support-chat/sessions/${sessionId}`);
        const session: ChatSession = await res.json();
        if (disposed || version !== reconciliationVersion) return;
        if (session.status === "closed") {
          setClosedSessionId(session.id);
          setMessages([]);
        } else {
          setClosedSessionId(previous => previous === session.id ? null : previous);
        }
        await queryClient.invalidateQueries({ queryKey: ["/api/support-chat/sessions"] });
      } catch {
        // Preserve the last confirmed state; a later event or reconnect retries.
      }
    };

    const handleClosed = () => { void reconcileSession(); };
    const handleReopened = () => { void reconcileSession(); };

    if (channel) {
      channel.bind("support-message", handleMsg);
      channel.bind("support-session-closed", handleClosed);
      channel.bind("support-session-reopened", handleReopened);
    }

    // Reconnect recovery: refetch session state if Pusher dropped and reconnected
    const unsubReconnect = subscribeToReconnect(() => {
      void reconcileSession();
    });

    return () => {
      disposed = true;
      if (channel) {
        channel.unbind("support-message", handleMsg);
        channel.unbind("support-session-closed", handleClosed);
        channel.unbind("support-session-reopened", handleReopened);
      }
      releaseSupportSessionChannel(sessionId);
      sessionChannelRef.current = null;
      unsubReconnect();
    };
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const createSessionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/support-chat/sessions", { subject: "Help request" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/support-chat/sessions"] });
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({ body, attachments, sessionId }: { body: string; attachments: SupportAttachment[]; sessionId?: string }) => {
      const sid = sessionId || activeSession?.id;
      if (!sid) return;
      const payload: any = { body };
      if (attachments.length > 0) payload.attachments = attachments;
      const res = await apiRequest("POST", `/api/support-chat/sessions/${sid}/messages`, payload);
      return res.json();
    },
  });

  const closeSessionMutation = useMutation({
    mutationFn: async () => {
      if (!activeSession?.id) return;
      const sessionId = activeSession.id;
      await apiRequest("PUT", `/api/support-chat/sessions/${sessionId}/close`);
      return sessionId;
    },
    onSuccess: (sessionId) => {
      if (!sessionId) return;
      setClosedSessionId(sessionId);
      setMessages([]);
      queryClient.invalidateQueries({ queryKey: ["/api/support-chat/sessions"] });
    },
  });

  const ratingMutation = useMutation({
    mutationFn: async ({ rating, comment }: { rating: number; comment: string }) => {
      if (!closedSessionId) return;
      await apiRequest("POST", `/api/support-chat/sessions/${closedSessionId}/rate`, { rating, comment });
    },
    onSuccess: () => {
      setClosedSessionId(null);
      setSelectedRating(0);
      setRatingComment("");
      queryClient.invalidateQueries({ queryKey: ["/api/support-chat/sessions"] });
    },
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setHasUnread(false);
    void refetchSession();
  }, [refetchSession]);

  const pendingMessageRef = useRef<{ body: string; attachments: SupportAttachment[] } | null>(null);

  const handleSend = useCallback(async () => {
    const trimmed = message.trim();
    if (!trimmed && pendingAttachments.length === 0) return;
    const bodyText = trimmed || (pendingAttachments.length > 0 ? `[${pendingAttachments.length} file(s) attached]` : "");
    const atts = [...pendingAttachments];
    setMessage("");
    setPendingAttachments([]);

    if (!activeSession) {
      pendingMessageRef.current = { body: bodyText, attachments: atts };
      const optimistic: ChatMessage = {
        id: `temp-${Date.now()}`,
        sessionId: "pending",
        senderId: user!.id,
        senderRole: user!.role,
        body: bodyText,
        attachments: atts.length > 0 ? atts : undefined,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
      createSessionMutation.mutate();
      textareaRef.current?.focus();
      return;
    }

    const optimistic: ChatMessage = {
      id: `temp-${Date.now()}`,
      sessionId: activeSession.id,
      senderId: user!.id,
      senderRole: user!.role,
      body: bodyText,
      attachments: atts.length > 0 ? atts : undefined,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    sendMessageMutation.mutate({ body: bodyText, attachments: atts });
    textareaRef.current?.focus();
  }, [message, activeSession, user, pendingAttachments]);

  if (!user || user.role === "super_admin") return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      await uploadFile(files[i]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <>
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-[60] rounded-full bg-primary text-primary-foreground w-14 h-14 flex items-center justify-center shadow-lg"
          data-testid="button-support-chat-open"
        >
          <MessageCircleQuestion className="w-6 h-6" />
          {hasUnread && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive rounded-full border-2 border-background" />
          )}
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-6 right-6 z-[60] w-[360px] max-w-[calc(100vw-2rem)]" data-testid="panel-support-chat">
          <Card className="flex flex-col h-[500px] max-h-[calc(100vh-4rem)] shadow-xl overflow-hidden">
            <div className="flex items-center justify-between gap-2 p-3 border-b bg-primary text-primary-foreground rounded-t-md">
              <div className="flex items-center gap-2 min-w-0">
                <MessageCircleQuestion className="w-5 h-5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">Support Chat</p>
                  <p className="text-[10px] opacity-80">
                    {activeSession?.status === "active" ? "Connected to support" : activeSession?.status === "waiting" ? "Waiting for agent..." : "Start a conversation"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {activeSession && activeSession.status !== "closed" && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-primary-foreground h-7 w-7"
                    onClick={() => closeSessionMutation.mutate()}
                    data-testid="button-support-chat-close-session"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-primary-foreground h-7 w-7"
                  onClick={() => setIsOpen(false)}
                  data-testid="button-support-chat-minimize"
                >
                  <Minimize2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {showRating ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <p className="text-sm font-medium">How was your support experience?</p>
                  <div className="flex items-center gap-1" data-testid="rating-stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setSelectedRating(star)}
                        className="p-0.5"
                        data-testid={`button-rate-${star}`}
                      >
                        <Star
                          className={`w-7 h-7 ${star <= selectedRating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`}
                        />
                      </button>
                    ))}
                  </div>
                  <Textarea
                    placeholder="Any additional feedback? (optional)"
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    className="text-sm"
                    rows={2}
                    data-testid="input-rating-comment"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setClosedSessionId(null);
                        queryClient.invalidateQueries({ queryKey: ["/api/support-chat/sessions"] });
                      }}
                      data-testid="button-skip-rating"
                    >
                      Skip
                    </Button>
                    <Button
                      size="sm"
                      disabled={!selectedRating || ratingMutation.isPending}
                      onClick={() => ratingMutation.mutate({ rating: selectedRating, comment: ratingComment })}
                      data-testid="button-submit-rating"
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageCircleQuestion className="w-10 h-10 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {activeSession?.status === "waiting"
                      ? "A support agent will be with you shortly. Feel free to type your question."
                      : "Send us a message and we'll help you out!"}
                  </p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMe = msg.senderId === user?.id;
                  const isBot = msg.senderRole === "support_bot";
                  const msgAttachments: SupportAttachment[] = Array.isArray(msg.attachments) ? msg.attachments : [];
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                      data-testid={`chat-message-${msg.id}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 ${
                          isMe
                            ? "bg-primary text-primary-foreground"
                            : isBot
                              ? "bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800"
                              : "bg-muted"
                        }`}
                      >
                        {!isMe && (
                          <p className={`text-[10px] font-medium mb-0.5 ${isBot ? "text-blue-600 dark:text-blue-400" : "opacity-70"}`}>
                            {isBot ? "Support Bot" : (msg.senderName || "Support")}
                          </p>
                        )}
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                        {msgAttachments.map((att, i) => (
                          <AttachmentBubble key={i} attachment={att} />
                        ))}
                        <p className={`text-[10px] mt-0.5 ${isMe ? "opacity-70" : "text-muted-foreground"}`}>
                          {formatTime(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {((!activeSession || activeSession.status !== "closed") && !showRating) && (
              <div className="p-3 border-t space-y-2">
                {pendingAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {pendingAttachments.map((att, i) => (
                      <div key={i} className="flex items-center gap-1 bg-muted rounded px-2 py-1 text-xs">
                        <Paperclip className="w-3 h-3" />
                        <span className="truncate max-w-[100px]">{att.filename}</span>
                        <button
                          onClick={() => setPendingAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                          className="ml-0.5 text-muted-foreground hover:text-foreground"
                          data-testid={`button-remove-attachment-${i}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="input-file-upload"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="shrink-0 h-9 w-9"
                    data-testid="button-attach-file"
                  >
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  <Textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    className="text-sm resize-none min-h-[36px] max-h-[100px]"
                    rows={1}
                    data-testid="input-support-chat-message"
                  />
                  <Button
                    size="icon"
                    onClick={handleSend}
                    disabled={(!message.trim() && pendingAttachments.length === 0) || sendMessageMutation.isPending || createSessionMutation.isPending || isUploading}
                    data-testid="button-support-chat-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

          </Card>
        </div>
      )}
    </>
  );
}
